import React, { useContext, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { PartnerFile } from "../../services/PartnerFileService";
import { FileExchangeGroup, FileTable, formatPhrase } from "./shared";

/**
 * getInitial
 * @param name
 */
function getInitial(name: string): string {
  return (name || "").trim().charAt(0).toUpperCase();
}

/**
 * CustomerFilesProps
 */
type CustomerFilesProps = {
  groups: FileExchangeGroup[];
  pendingFileId: string;
  onDownload: (file: PartnerFile) => void;
};

/**
 * CustomerFiles
 * what the logged in partner receives: one group per supplier, each a flat list
 * of the files that supplier releases to it. no storage figure -- the quota
 * belongs to whoever offers the files, not to whoever takes them
 * @param props
 * @constructor
 */
export const CustomerFiles: React.FC<CustomerFilesProps> = ({
  groups,
  pendingFileId,
  onDownload
}) => {
  const {
    I18N,
    services: {
      servicePartner
    }
  } = useContext(AppContext);

  const [brokenLogos, setBrokenLogos] = useState<{ [supplierId: string]: boolean }>({});

  /**
   * render
   */
  return (
    <>
      {groups.map((group) => {
        const hasLogo = !!group.hashId && !brokenLogos[group.supplierId],
          initial = getInitial(group.name);

        return (
          <div
            key={group.supplierId}
            data-file-exchange-group
          >
            <div data-file-exchange-group-head>
              <div data-file-exchange-logo>
                {hasLogo ? (
                  <img
                    src={servicePartner.getCompanyLogoUrl(group.hashId)}
                    /* the supplier name sits in the heading right next to it,
                       so the logo carries no information of its own */
                    alt=""
                    loading="lazy"
                    onError={() => setBrokenLogos((current) => ({
                      ...current,
                      [group.supplierId]: true
                    }))}
                  />
                ) : (
                  <span
                    data-file-exchange-logo-fallback
                    aria-hidden="true"
                  >
                    {initial}
                  </span>
                )}
              </div>

              <h2 data-file-exchange-supplier>{group.name}</h2>
            </div>

            {!group.files.length ? (
              <p data-file-exchange-empty>
                {I18N.t("Dieser Lieferant stellt derzeit keine Dateien bereit.")}
              </p>
            ) : (
              <FileTable
                files={group.files}
                label={formatPhrase(I18N.t("Dateien von %s"), [group.name])}
                variant="supplier"
                pendingFileId={pendingFileId}
                onDownload={onDownload}
              />
            )}
          </div>
        );
      })}

      {/* the file list is cached on the server for five minutes, so a file a
          supplier just uploaded is not here yet */}
      {!!groups.length && (
        <p data-file-exchange-hint>
          {I18N.t("Neu hochgeladene Dateien erscheinen mit wenigen Minuten Verzögerung.")}
        </p>
      )}
    </>
  );
};
